import { useState } from 'react';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/SEOHead';

import CallScheduleModal from '../../../components/feature/CallScheduleModal';

const treatments = [
  {
    icon: 'ri-tooth-line',
    title: 'Dental Veneers',
    description: 'Transform your smile with porcelain or composite veneers',
    duration: '2–3 visits',
    details: ['Porcelain or composite options', 'Custom color matching', 'Minimal tooth preparation', '10–15 year lifespan', 'Stain-resistant material'],
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Dental Implants',
    description: 'Permanent tooth replacement with titanium implants',
    duration: '3–6 months',
    details: ['Titanium implant posts', 'Custom crown placement', 'Bone grafting if needed', 'Lifetime warranty', '98% success rate'],
  },
  {
    icon: 'ri-star-smile-line',
    title: 'Smile Makeover',
    description: 'Complete smile transformation with multiple procedures',
    duration: '1–2 weeks',
    details: ['Comprehensive assessment', 'Multiple procedures combined', 'Digital smile design', 'Before/after preview', 'Complete transformation'],
  },
  {
    icon: 'ri-align-center',
    title: 'Orthodontics',
    description: 'Braces and clear aligners for perfect teeth alignment',
    duration: '12–24 months',
    details: ['Metal or ceramic braces', 'Clear aligner options', 'Regular adjustments', 'Retainer included', 'Progress monitoring'],
  },
  {
    icon: 'ri-contrast-2-line',
    title: 'Teeth Whitening',
    description: 'Professional whitening for a brighter smile',
    duration: '1 hour',
    details: ['Laser whitening technology', 'Up to 8 shades lighter', 'Safe for enamel', 'Long-lasting results', 'Touch-up kit included'],
  },
  {
    icon: 'ri-first-aid-kit-line',
    title: 'Root Canal',
    description: 'Pain-free root canal treatment with modern techniques',
    duration: '1–2 visits',
    details: ['Advanced anesthesia', 'Microscopic precision', 'Same-day treatment', 'Crown placement', 'Pain-free procedure'],
  },
];

const tabs = ['overview', 'treatments', 'benefits', 'process'];

export default function DentalCare() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const siteUrl = import.meta.env.VITE_SITE_URL || '';

  const handleViewTreatments = () => {
    setActiveTab('treatments');
    const el = document.getElementById('tabs-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/services/dental-care`,
        url: `${siteUrl}/services/dental-care`,
        name: 'Dental Care Services in Turkey — Plexus Alliance',
        description: 'Premium dental treatments in Istanbul, Turkey — veneers, implants, smile makeovers, orthodontics & whitening. Save 60–70% vs USA/UK prices. JCI-accredited clinics, doctor-supervised care.',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
            { '@type': 'ListItem', position: 3, name: 'Dental Care', item: `${siteUrl}/services/dental-care` },
          ],
        },
      },
      {
        '@type': 'MedicalClinic',
        name: 'Plexus Alliance — Dental Care in Istanbul',
        url: `${siteUrl}/services/dental-care`,
        description: 'JCI-accredited dental clinics in Istanbul, Turkey offering implants, veneers, smile makeovers, orthodontics, whitening and root canal treatments with independent doctor supervision.',
        medicalSpecialty: 'Dentistry',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Istanbul',
          addressCountry: 'TR',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Dental Treatments',
          itemListElement: treatments.map((tr, i) => ({
            '@type': 'Offer',
            position: i + 1,
            itemOffered: {
              '@type': 'MedicalProcedure',
              name: tr.title,
              description: tr.description,
            },
          })),
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Dental Care Services in Turkey | Implants, Veneers & Smile Makeovers — Plexus Alliance"
        description="Premium dental treatments in Istanbul, Turkey — veneers, implants, smile makeovers, orthodontics & whitening. Save 60–70% vs USA/UK prices. JCI-accredited clinics, doctor-supervised care."
        keywords="dental care Turkey, dental veneers Istanbul, dental implants Turkey, smile makeover Istanbul, teeth whitening Turkey, orthodontics Turkey"
        canonical={`${siteUrl}/services/dental-care`}
        schema={schema}
      />
      <Navigation />

      {/* ── HERO ── */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6588cb859f95970e637a8c33d464baaa.jpg"
            alt="Luxury dental clinic"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px bg-amber-400/60" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Premium Dental Care
            </span>
            <div className="w-12 h-px bg-amber-400/60" />
          </div>
          <h1
            className="text-5xl md:text-7xl font-normal text-white mb-6 leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            World-Class
            <br />
            <em className="text-amber-300 not-italic">Dental Care.</em>
          </h1>
          <p className="text-base md:text-lg text-white/50 mb-12 max-w-2xl mx-auto leading-relaxed font-light" style={{ fontFamily: "'Inter', sans-serif" }}>
            Comprehensive dental treatments from implants to smile makeovers with cutting-edge technology in Istanbul.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-amber-400 hover:bg-amber-300 text-black px-9 py-4 font-semibold text-sm tracking-wide transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-calendar-check-line mr-2" />
              Schedule Consultation
            </button>
            <button
              onClick={handleViewTreatments}
              className="border border-white/20 text-white/70 hover:text-white hover:border-white/40 px-9 py-4 font-medium text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-tooth-line mr-2" />
              View Treatments
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-2xl text-white/30" />
        </div>
      </section>

      {/* ── TABS ── */}
      <section id="tabs-section" className="py-20 md:py-28 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Tab switcher */}
          <div className="flex flex-wrap gap-px bg-white/5 border border-white/5 mb-14 md:mb-20 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-xs font-medium tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-amber-400 text-black'
                    : 'bg-[#0f0f0f] text-white/30 hover:text-white/60 hover:bg-white/[0.03]'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Overview */}
          {activeTab === 'overview' && (
            <div>
              <div className="grid md:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-14">
                {[
                  { value: '60–70%', label: 'Cost Savings', icon: 'ri-percent-line' },
                  { value: '500+', label: 'Accredited Clinics', icon: 'ri-hospital-line' },
                  { value: '50K+', label: 'Happy Patients Yearly', icon: 'ri-user-heart-line' },
                ].map((s, i) => (
                  <div key={i} className="bg-[#0f0f0f] p-10 md:p-14 text-center">
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
                      <i className={`${s.icon} text-2xl text-amber-400/60`} />
                    </div>
                    <div className="text-3xl md:text-4xl font-normal text-amber-300 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
                    <div className="text-xs text-white/25 tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <p className="text-white/40 text-base font-light leading-relaxed mb-10 max-w-3xl" style={{ fontFamily: "'Inter', sans-serif" }}>
                Turkey has become a global leader in dental tourism, offering world-class treatments at a fraction of Western prices. Our partner clinics use the latest technology and materials, with dentists trained in Europe and the US. Every clinic is JCI-accredited, ensuring international quality standards.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-award-line', text: 'Significant savings compared to US/UK prices' },
                  { icon: 'ri-shield-check-line', text: 'JCI-accredited dental clinics' },
                  { icon: 'ri-user-star-line', text: 'Experienced international dentists' },
                  { icon: 'ri-shield-star-line', text: 'Lifetime warranty on implants' },
                  { icon: 'ri-hotel-line', text: 'Free accommodation packages' },
                  { icon: 'ri-plane-line', text: 'Airport VIP transfer included' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 border border-white/5 p-4 hover:bg-white/[0.02] transition-colors">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-amber-400/60 text-lg`} />
                    </div>
                    <span className="text-white/50 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Treatments */}
          {activeTab === 'treatments' && (
            <div id="treatments-section" className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
              {treatments.map((treatment, index) => (
                <div key={index} className="bg-[#0f0f0f] p-8 hover:bg-[#161616] transition-all group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center mb-5">
                    <i className={`${treatment.icon} text-2xl text-white/20 group-hover:text-amber-400 transition-colors duration-300`} />
                  </div>
                  <h3 className="text-base font-semibold text-white/80 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{treatment.title}</h3>
                  <p className="text-white/30 text-sm font-light mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>{treatment.description}</p>
                  <div className="flex items-center text-xs text-white/20 mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <i className="ri-time-line mr-2" />
                    <span>{treatment.duration}</span>
                  </div>
                  <div className="space-y-2 mb-6">
                    {treatment.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-amber-400/50 text-sm mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-white/30 font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{detail}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/book-treatment')}
                    className="w-full py-3 bg-amber-400 hover:bg-amber-300 text-black font-semibold text-xs tracking-wide transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Book This Treatment
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Benefits */}
          {activeTab === 'benefits' && (
            <div>
              <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center mb-14">
                <div className="relative h-72 md:h-96 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=happy%20patient%20beautiful%20white%20smile%20after%20dental%20treatment%20dark%20elegant%20clinic%20Istanbul%20Turkey%20warm%20amber%20lighting%20satisfied%20confident%20portrait%20moody%20editorial%20photography%20premium%20healthcare&width=700&height=600&seq=dentalcare-benefits-dark-01&orientation=landscape"
                    alt="Dental Treatment Results"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f]/40 to-transparent" />
                </div>
                <div className="space-y-6">
                  {[
                    { icon: 'ri-shield-star-line', title: 'Quality Guarantee', desc: 'Lifetime warranty on implants and long-term guarantees on all treatments' },
                    { icon: 'ri-hotel-line', title: 'Accommodation', desc: 'Luxury hotel stays included in comprehensive packages' },
                    { icon: 'ri-car-line', title: 'Transportation', desc: 'VIP airport transfers and clinic transportation' },
                    { icon: 'ri-global-line', title: 'Language Support', desc: 'Professional medical interpreters throughout your stay' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-5 border border-white/5 p-5 hover:bg-white/[0.02] transition-colors">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-amber-400/60`} />
                      </div>
                      <div>
                        <h3 className="text-base font-medium text-white/70 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{item.title}</h3>
                        <p className="text-white/30 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Process */}
          {activeTab === 'process' && (
            <div>
              <div className="space-y-px border border-white/5 mb-14">
                {[
                  { step: 1, title: 'Initial Consultation', desc: 'Send us your dental X-rays and photos for free assessment', icon: 'ri-chat-smile-3-line' },
                  { step: 2, title: 'Treatment Plan', desc: 'Receive detailed treatment plan with costs and timeline', icon: 'ri-file-list-3-line' },
                  { step: 3, title: 'Travel Arrangements', desc: 'We arrange flights, accommodation, and airport transfers', icon: 'ri-plane-line' },
                  { step: 4, title: 'Treatment', desc: 'Receive world-class dental care in our partner clinics', icon: 'ri-hospital-line' },
                  { step: 5, title: 'Follow-up', desc: 'Lifetime support and warranty on all treatments', icon: 'ri-customer-service-line' },
                ].map((item) => (
                  <div key={item.step} className="bg-[#0f0f0f] p-6 md:p-8 flex items-start gap-6 hover:bg-[#161616] transition-colors">
                    <div className="text-3xl font-normal text-amber-400/20 w-10 flex-shrink-0" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {String(item.step).padStart(2, '0')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <i className={`${item.icon} text-amber-400/60 text-lg`} />
                        <h3 className="text-base font-medium text-white/70" style={{ fontFamily: "'Inter', sans-serif" }}>{item.title}</h3>
                      </div>
                      <p className="text-white/30 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border border-white/5 p-8 md:p-10">
                <h3 className="text-xl font-normal text-white/70 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>What to Bring</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {['Recent dental X-rays (if available)', 'Medical history and current medications', 'Valid passport and travel insurance', 'Photos of your current smile', 'List of any dental concerns or goals', 'Comfortable clothing for appointments'].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-amber-400/50 text-sm mt-0.5 flex-shrink-0" />
                      <span className="text-white/40 text-sm font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20dental%20clinic%20Istanbul%20Turkey%20dark%20elegant%20interior%20warm%20amber%20gold%20lighting%20sophisticated%20medical%20environment%20moody%20atmospheric%20editorial%20photography%20premium%20healthcare%20high-end%20aesthetic%20wide%20angle&width=1440&height=700&seq=dentalcare-cta-dark-01&orientation=landscape"
            alt="Luxury dental clinic"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px bg-amber-400/60" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Transform Your Smile
            </span>
            <div className="w-12 h-px bg-amber-400/60" />
          </div>
          <h2
            className="text-4xl md:text-6xl font-normal text-white leading-[1.05] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            Ready to transform
            <br />
            <em className="text-amber-300 not-italic">your smile?</em>
          </h2>
          <p className="text-white/40 text-base font-light mb-12 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Book your dental treatment today and save up to 70% with doctor supervision.
          </p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/treatments')}
            className="bg-amber-400 hover:bg-amber-300 text-black px-10 py-4 font-semibold text-sm tracking-wide transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            View All Treatments
            <i className="ri-arrow-right-line ml-2" />
          </button>
        </div>
      </section>

      <CallScheduleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <Footer onBookClick={() => window.REACT_APP_NAVIGATE('/book-treatment')} />
    </div>
  );
}
